import {FC, useEffect, useState, useContext} from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {Control, FieldValues} from 'react-hook-form';
import {
  IeditTeamMember,
  TEAM_MEMBER_NAME_MAX_LENGTH,
  TeamMemberInfoFormInputKey,
} from '../../pages/user-management/models';
import {AurFormController} from '../../common/aur-form-controller/AurFormController';
import {AurIpAddress} from '../../common/aur-ip-address/AurIpAddress';
import {AurSwitch} from '../../common/aur-switch/AurSwitch';
import {t} from 'i18next';
import AurIcon, {IconColors, IconNames} from '../../common/aur-icon/AurIcon';
import AurCheckbox from '../../common/aur-checkbox/AurCheckbox';
import {AuthorizationContext} from '../../../contexts/authorizationContext/AuthorizationContext';
import {Permissions, Resources} from '../../../utils/authorization';
import {useFieldFocusStyle} from '../../../hooks/utils/useFieldFocusStyle';
import {setAnalyticsInfo} from '../../../redux/slices/teamMemberSlice';
import {useDispatch} from 'react-redux';

/**
 * Defined the AurUserFormFields component
 * it is a common component for the genrarting the user form fields.
 */

interface AurUserFormFieldsProps {
  control: unknown | Control<IeditTeamMember>;
  register: any;
  errors: any;
  setValue: any;
  isValid?: boolean;
  buttonVisible?: boolean;
  leftGridSize?: number;
  rightGridSize?: number;
  activeVisible?: boolean;
  isBulkUserEdit?: boolean;
  teamMemberData: {
    ipAddressList: string[] | undefined;
    isLimitIp: boolean;
    ipAddressEditState?: boolean;
    ipAddressError?: boolean;
    isSysAdmin?: boolean;
  };
  onSysAdminCheck?: (checked: boolean) => void;
}

const AurUserFormFields: FC<AurUserFormFieldsProps> = (props) => {
  const {
    control,
    register,
    errors,
    setValue,
    isValid,
    buttonVisible = true,
    leftGridSize = 5,
    rightGridSize = 5,
    activeVisible = true,
    teamMemberData,
    isBulkUserEdit = false,
  } = props;

  const [isLimitIp, setIsLimitIp] = useState(teamMemberData.isLimitIp);
  const dispatch = useDispatch();
  const {
    borderClass: firstNameBorderClass,
    focusHandler: firstNameFocusHandler,
    mouseKeyDownHandler: firstNameMouseKeyDownHandler,
  } = useFieldFocusStyle({
    tabFocusClass: 'tabFocusedBorder',
    mouseFocusClass: 'mouseFocusedBorder',
  });
  const {
    borderClass: lastNameBorderClass,
    focusHandler: lastNameFocusHandler,
    mouseKeyDownHandler: lasttNameMouseKeyDownHandler,
  } = useFieldFocusStyle({
    tabFocusClass: 'tabFocusedBorder',
    mouseFocusClass: 'mouseFocusedBorder',
  });
  const {
    borderClass: emailBorderClass,
    focusHandler: emailFocusHandler,
    mouseKeyDownHandler: emailMouseKeyDownHandler,
  } = useFieldFocusStyle({
    tabFocusClass: 'tabFocusedBorder',
    mouseFocusClass: 'mouseFocusedBorder',
  });

  const auth = useContext(AuthorizationContext);

  useEffect(() => {
    setIsLimitIp(teamMemberData.isLimitIp);
  }, [teamMemberData.isLimitIp]);

  const sysAdminText = () => {
    return (
      <div>
        <div className="sys-admin-note">
          <Typography className="mist-color-500">
            {t('addEditTeamMember.form.sysAdminNote')}
          </Typography>
        </div>
      </div>
    );
  };

   const handleKeyDown = (event: any) => {
    if (event.key === ' ') {
      // Prevent space from being entered
      event.preventDefault();
    }
  };

  return (
    <>
      <Grid
        container
        spacing={10}
        className="user-form team-member-info-settings"
      >
        <Grid
          item
          sm={12}
          md={leftGridSize}
          className="form-content userform-container"
        >
          <Typography className="typo-text subhead3_size subhead-text-color">
            {t('addEditTeamMember.teamMemberInfoTitle')}
          </Typography>

          <AurFormController
            control={control as unknown as Control<FieldValues>}
            name={TeamMemberInfoFormInputKey.firstName}
          >
            <TextField
              className={`team-member-info-field ${firstNameBorderClass}`}
              inputProps={{
                'data-testid': 'first-name',
                maxLength: TEAM_MEMBER_NAME_MAX_LENGTH,
              }}
              InputProps={{
                disableUnderline: true,
                endAdornment: errors?.firstName?.message ? (
                  <InputAdornment position="end">
                    <AurIcon
                      icon={IconNames.Error}
                      color={IconColors.Critical}
                    />
                  </InputAdornment>
                ) : null,

                className: errors?.firstName?.message && 'inputError',
              }}
              variant="filled"
              label={t('addEditTeamMember.form.firstName')}
              helperText={errors?.firstName?.message}
              InputLabelProps={{
                className: errors?.firstName?.message && 'label-error',
              }}
              FormHelperTextProps={{
                className: errors?.firstName?.message && 'label-error',
              }}
              onFocus={firstNameFocusHandler}
              onMouseDown={firstNameMouseKeyDownHandler}
             onKeyDown={handleKeyDown}
            />
          </AurFormController>
          <AurFormController
            control={control as unknown as Control<FieldValues>}
            name={TeamMemberInfoFormInputKey.lastName}
          >
            <TextField
              className={`team-member-info-field ${lastNameBorderClass}`}
              variant="filled"
              inputProps={{
                'data-testid': 'last-name',
                maxLength: TEAM_MEMBER_NAME_MAX_LENGTH,
              }}
              InputProps={{
                disableUnderline: true,
                endAdornment: errors?.lastName?.message ? (
                  <InputAdornment position="end">
                    <AurIcon
                      icon={IconNames.Error}
                      color={IconColors.Critical}
                    />
                  </InputAdornment>
                ) : null,
                className: errors?.lastName?.message && 'inputError',
              }}
              label={t('addEditTeamMember.form.lastName')}
              helperText={errors?.lastName?.message}
              InputLabelProps={{
                className: errors?.lastName?.message && 'label-error',
              }}
              FormHelperTextProps={{
                className: errors?.lastName?.message && 'label-error',
              }}
              onFocus={lastNameFocusHandler}
              onMouseDown={lasttNameMouseKeyDownHandler}
            />
          </AurFormController>
          <AurFormController
            control={control as unknown as Control<FieldValues>}
            name={TeamMemberInfoFormInputKey.email}
          >
            <TextField
              className={`team-member-info-field ${emailBorderClass}`}
              inputProps={{'data-testid': 'email'}}
              InputProps={{
                disableUnderline: true,
                endAdornment: errors?.email?.message ? (
                  <InputAdornment position="end">
                    <AurIcon
                      icon={IconNames.Error}
                      color={IconColors.Critical}
                    />
                  </InputAdornment>
                ) : null,
                className: errors?.email?.message && 'inputError',
              }}
              variant="filled"
              label={t('addEditTeamMember.form.email')}
              helperText={errors?.email?.message}
              InputLabelProps={{
                className: errors?.email?.message && 'label-error',
              }}
              FormHelperTextProps={{
                className: errors?.email?.message && 'label-error',
              }}
              onFocus={emailFocusHandler}
              onMouseDown={emailMouseKeyDownHandler}
            />
          </AurFormController>
          {auth.can(Permissions.Add, Resources.AdminUsers) ? (
            buttonVisible && (
              <>
                <div className="left-align-center-y">
                  <FormControlLabel
                    control={<AurCheckbox />}
                    checked={teamMemberData.isSysAdmin}
                    className="sys-admin-checkbox"
                    onChange={(event: any) => {
                      teamMemberData.isSysAdmin = event.target.checked;
                      props.onSysAdminCheck &&
                        props.onSysAdminCheck(event.target.checked);
                      if (event.target.checked) {
                        dispatch(
                          setAnalyticsInfo({
                            userRole: t('gaTracking.users.sysAdmin'),
                            numberOfProject: '',
                          })
                        );
                      }
                    }}
                    label={
                      <Typography className="subhead-text-color">
                        {t('addEditTeamMember.form.sysAdmin')}
                      </Typography>
                    }
                  />
                </div>
                {teamMemberData.isSysAdmin ? sysAdminText() : <></>}
              </>
            )
          ) : (
            <></>
          )}
        </Grid>
        <Grid item sm={12} md={rightGridSize} className="team-member-settings">
          <Typography className="typo-text subhead3_size subhead-text-color">
            {t('addEditTeamMember.teamMemberDesignationTitle')}
          </Typography>
          <Box>
            <Box className="designation-section">
              {activeVisible && (
                <Box>
                  <FormControlLabel
                    control={
                      <>
                        {isBulkUserEdit ? (
                          <AurFormController
                            control={control as unknown as Control<FieldValues>}
                            name={TeamMemberInfoFormInputKey.isActive}
                          >
                            <AurSwitch
                              dataTestId="is-active-switch"
                              {...register(TeamMemberInfoFormInputKey.isActive)}
                            />
                          </AurFormController>
                        ) : (
                          <AurSwitch disabled dataTestId="is-active-switch" />
                        )}
                      </>
                    }
                    label={t('addEditTeamMember.form.active')}
                  />
                </Box>
              )}
              <Box>
                <FormControlLabel
                  control={
                    <AurFormController
                      control={control as unknown as Control<FieldValues>}
                      name={TeamMemberInfoFormInputKey.isBillable}
                    >
                      <AurSwitch
                        dataTestId="is-billable-switch"
                        {...register(TeamMemberInfoFormInputKey.isBillable)}
                      />
                    </AurFormController>
                  }
                  label={t('addEditTeamMember.form.billable')}
                />
                {errors?.isBillable?.message && (
                  <FormHelperText
                    className="label-error error-text-billable"
                    error
                  >
                    {errors?.isBillable?.message}
                  </FormHelperText>
                )}
              </Box>
              <Box>
                <FormControlLabel
                  control={
                    <AurFormController
                      control={control as unknown as Control<FieldValues>}
                      name={TeamMemberInfoFormInputKey.isLimitIp}
                    >
                      <AurSwitch
                        dataTestId="is-limit-ip-switch"
                        {...register(TeamMemberInfoFormInputKey.isLimitIp, {
                          onChange: (e: any) => {
                            setIsLimitIp(e.target.value);
                          },
                        })}
                      />
                    </AurFormController>
                  }
                  label={t('addEditTeamMember.form.limitIpAccess')}
                />
              </Box>
            </Box>
            {isLimitIp && (
              <AurIpAddress
                ipAdressList={teamMemberData.ipAddressList}
                onIpListUpdate={(value: string[]) => {
                  setValue(TeamMemberInfoFormInputKey.ipAddressList, value, {
                    shouldValidate: true,
                    shouldDirty: true,
                  });
                }}
                ipAddressEditState={teamMemberData.ipAddressEditState}
                ipAddressError={teamMemberData.ipAddressError}
              />
            )}
          </Box>
        </Grid>
      </Grid>
      {buttonVisible && (
        <Grid item sm={12} md={12} className="save-button text-right">
          <Button
            className="button-continue typo-text buttonLarge_size submit-btn team-member-save-button"
            type="submit"
            disabled={!isValid}
          >
            {t('addEditTeamMember.form.saveAndContine')}
            <AurIcon icon={IconNames.ChevronRight} />
          </Button>
        </Grid>
      )}
    </>
  );
};

export default AurUserFormFields;
